import React, { useState } from "react";
import { CicdFaqs } from "../../../components/faqData.js";
import ContactUs from "../../../components/ContactUs.js";
import { Helmet } from "react-helmet-async";

const services = [
  {
    logo_images: "../assets/icons/helm-chart/service-1.svg",
    title: "Chart Creation",
    description:
      "Design production-ready Helm charts from scratch with reusable templates, helper functions, and best practices for values management, hooks, and lifecycle events.",
  },
  {
    logo_images: "../assets/icons/helm-chart/service-2.png",
    title: "Chart Customization",
    description:
      "Extend existing community or vendor charts with environment-specific overrides, templated values files, and opinionated defaults aligned to your infrastructure standards.",
  },
  {
    logo_images: "../assets/icons/helm-chart/service-3.png",
    title: "Chart Versioning",
    description:
      "Implement semantic versioning, changelog automation, and chart testing workflows to ensure safe upgrades across environments while maintaining backwards compatibility.",
  },
  {
    logo_images: "../assets/icons/helm-chart/service-4.svg",
    title: "Helm Repository Setup",
    description:
      "Configure private Helm repositories on S3, GCS, Azure Blob, or Artifact Hub with authentication, automated index updates, and pipeline integrations.",
  },
  {
    logo_images: "../assets/icons/helm-chart/service-5.png",
    title: "Helm in CI/CD",
    description:
      "Embed Helm releases into CI/CD pipelines (GitHub Actions, GitLab CI, Jenkins, ArgoCD) with linting, dry-runs, and automated rollbacks for zero-downtime deployments.",
  },
];

const benefits = [
  "Standardized deployments with reusable, templated manifests",
  "Rapid rollouts and rollbacks using version-controlled charts",
  "Separation of configuration via values files per environment",
  "Simplified dependency management for complex applications",
  "Improved collaboration between DevOps and product teams",
  "Seamless integration with GitOps and CI/CD workflows",
];

const tools = [
  "Helm CLI",
  "Chart Museum",
  "Artifact Hub",
  "Kustomize",
  "ArgoCD",
  "FluxCD",
  "GitHub Actions",
  "GitLab CI",
  "Jenkins",
  "Skaffold",
  "Terraform",
  "Vault",
];

const useCases = [
  {
    title: "Multi-Environment Platform Rollout",
    description:
      "Created modular Helm charts for 20+ microservices with environment-specific values, enabling automated promotion from dev to prod and reducing deployment time by 80%.",
  },
  {
    title: "Enterprise Marketplace Packaging",
    description:
      "Packaged an ISV product as Helm charts distributed via private ChartMuseum with license enforcement, resulting in zero-touch customer installations.",
  },
  {
    title: "GitOps Release Automation",
    description:
      "Integrated Helm with ArgoCD for GitOps workflows, providing declarative releases, health checks, and automated rollbacks across five Kubernetes clusters.",
  },
  {
    title: "Regulated Environment Compliance",
    description:
      "Implemented chart testing, signed artifacts, and provenance metadata to meet SOC 2 and HIPAA deployment requirements across cloud regions.",
  },
];

const reasons = [
  "Helm Certified engineers with deep Kubernetes experience",
  "End-to-end services covering design, automation, and lifecycle management",
  "Proven accelerators for chart scaffolding, linting, and testing",
  "Security-first approach with signed charts and secret management integrations",
  "Multi-cloud expertise across AWS, Azure, and Google Cloud ecosystems",
  "24/7 support for mission-critical Helm-based delivery pipelines",
];

const HelmCharts = () => {
  const [actIndex, setActIndex] = useState(null);

  const toggleFAQ = (index) => {
    setActIndex(index === actIndex ? null : index);
  };

  const scrollToContact = () => {
    document
      .getElementById("contact-section")
      .scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <Helmet>
        <meta
          name="description"
          content="Ayanshtech Solutions — expert Helm chart services for Kubernetes. Chart creation, customization, versioning, repositories, and CI/CD automation."
        />
        <meta name="copyright" content="codeglim" />
        <meta
          property="og:title"
          content="Helm Charts | Kubernetes Package Management | Ayanshtech Solutions"
        />
        <meta property="og:site_name" content="Ayanshtech Solutions" />
        <meta
          property="og:url"
          content="http://ayanshtechnology.com/devops-helm-charts"
        />
        <meta
          property="og:description"
          content="Ayanshtech Solutions — expert Helm chart services for Kubernetes. Chart creation, customization, versioning, repositories, and CI/CD automation."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="http://ayanshtechnology.com/assets/logo.png"
        />

        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@Ayanshtech Solutions" />
        <meta
          name="twitter:title"
          content="Helm Charts | Kubernetes Package Management | Ayanshtech Solutions"
        />
        <meta
          name="twitter:description"
          content="Ayanshtech Solutions — expert Helm chart services for Kubernetes. Chart creation, customization, versioning, repositories, and CI/CD automation."
        />
        <meta
          name="twitter:image"
          content="http://ayanshtechnology.com/assets/logo.png"
        />

        <link
          rel="canonical"
          href="http://ayanshtechnology.com/devops-helm-charts"
        />
        <title>
          Helm Charts | Kubernetes Package Management | Ayanshtech Solutions
        </title>
      </Helmet>
      <div className="flex flex-col items-center justify-center mb-6 p-6">
        {/* Hero */}
        <div className="py-5 w-full">
          <div className="flex flex-col lg:flex-row items-center lg:space-x-16">
            <div className="lg:w-3/4 lg:text-left p-4">
              <h1 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4">
                Helm Charts
              </h1>
              <p className="sm:text-base md:text-lg lg:text-lg mb-3">
                Standardize Kubernetes deployments with Helm — the package
                manager for container-native applications. Ayanshtech Solutions
                designs, builds, and automates Helm charts that keep your
                workloads consistent across environments while accelerating
                releases.
              </p>
              <p className="sm:text-base md:text-lg lg:text-lg leading-relaxed mb-3">
                From chart scaffolding and customization to repository
                management and CI/CD integration, our experts ensure your Helm
                ecosystem follows industry best practices, security controls,
                and compliance requirements.
              </p>
              <p className="sm:text-base md:text-lg lg:text-lg leading-relaxed mb-3">
                Whether you&apos;re packaging microservices, SaaS platforms, or
                internal tooling, we help you ship repeatable, versioned, and
                observable deployments backed by GitOps-ready workflows.
              </p>
              <button
                onClick={scrollToContact}
                className="bg-green-600 text-white hover:bg-yellow-500 hover:text-green-700 hover:border hover:border-yellow-500 font-semibold py-2 px-4 rounded-full mt-6"
              >
                Get Started
              </button>
            </div>
            <div className="flex w-full md:w-1/2 justify-center mb-12 lg:mb-0">
              <img
                src="../assets/images/devops-pages/helm-banner.png"
                alt="Helm Charts"
                className="w-full h-auto sm:w-3/4 md:w-2/3 lg:w-full object-contain"
              />
            </div>
          </div>
        </div>

        {/* About section */}
        {/* <div className="mb-5 mt-10 p-4">
          <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
            About Helm & Package Management
          </h2>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            Helm is the package manager for Kubernetes, enabling teams to define,
            install, and upgrade even the most complex applications through
            reusable charts. Charts bundle manifests, templates, dependencies,
            and configuration values into a single portable artifact that can be
            versioned, shared, and automated.
          </p>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            With Helm, platform and DevOps teams achieve consistent deployments,
            simplified rollbacks, and streamlined collaboration across
            environments. Paired with GitOps and CI/CD practices, Helm becomes
            the backbone of modern Kubernetes delivery pipelines.
          </p>
        </div> */}

        {/* Services */}
        <div className="text-center mb-5 mt-12">
          <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
            Helm Chart Services
          </h2>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            Comprehensive services covering every stage of the Helm lifecycle —
            from creation and customization to automation and release
            management.
          </p>
        </div>
        <div className="rounded-3xl">
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service) => (
                <div
                  key={service.title}
                  className="relative flex flex-col space-y-4 rounded-3xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
                 
                >
                  <div className="flex items-center gap-2">
                    <div className="w-[60px] h-[60px]  ">
                      <img src={service.logo_images} className="w-full" alt="" />
                    </div>
                  <h3 className="text-lg font-bold">{service.title}</h3>
                  </div>
                  <p className="text-lg text-gray-700">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Benefits */}
        <div className="text-center mb-5 mt-20">
          <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
            Benefits of Helm
          </h2>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            Helm standardizes Kubernetes deployments and delivers tangible gains
            in speed, consistency, and governance.
          </p>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit) => (
              <div
                key={benefit}
                className="flex items-start space-x-4 rounded-2xl shadow-md p-4 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
              >
                <div className="h-3 w-3 rounded-full bg-blue-600 mt-2 flex-shrink-0" />
                <p className="text-lg text-gray-700">{benefit}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Tools */}
        <div className="text-center mb-5 mt-20">
          <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
            Tools We Use
          </h2>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            We combine Helm with best-in-class tooling for packaging,
            automation, and observability.
          </p>
        </div>
        <div className="p-4">
          <div className="flex flex-wrap justify-center gap-4">
            {tools.map((tool) => (
              <span
                key={tool}
                className=" group
        relative z-0
        px-4 py-2 rounded-full font-semibold
        bg-red-50 text-blue-700 border border-blue-100
        overflow-hidden cursor-pointer transition-all
        
        before:absolute before:inset-0
        before:bg-gradient-to-r before:from-blue-500 before:to-green-500
        before:translate-x-[-101%]
        before:transition-transform before:duration-300
        hover:before:translate-x-0"
              >
                <span  className="relative z-10 group-hover:text-white">
                {tool}
                </span>
              </span>
            ))}
          </div>
        </div>

        {/* Use cases */}
        <div className="text-center mb-5 mt-20">
          <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
            Real-World Use Cases
          </h2>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            How enterprises leverage Helm with Ayanshtech to streamline
            Kubernetes delivery.
          </p>
        </div>
        <div className="rounded-3xl">
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              {useCases.map((useCase) => (
                <div
                  key={useCase.title}
                  className="relative flex flex-col space-y-4 rounded-3xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
                 
                >
                  <h3 className="text-lg font-bold text-gray-900">
                    {useCase.title}
                  </h3>
                  <p className="text-lg text-gray-700">
                    {useCase.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Why Choose Us */}
        <div className="p-6 sm:p-8 md:p-12 lg:p-16 w-full rounded-3xl mt-10">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              Why Choose Ayanshtech for Helm
            </h2>
            <p className="sm:text-base md:text-lg lg:text-xl max-w-7xl mx-auto mb-4">
              Our Helm specialists combine Kubernetes mastery with DevOps
              automation to ship reliable release frameworks tailored to your
              organization.
            </p>
          </div>
          <div className="mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-7xl mx-auto">
              {reasons.map((reason) => (
                <div
                  key={reason}
                  className="flex items-start space-x-4 rounded-2xl shadow-md p-4 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
                >
                  <div className="h-3 w-3 rounded-full bg-green-600 mt-2 flex-shrink-0" />
                  <p className="text-lg text-gray-700">{reason}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="text-center">
            <button
              onClick={scrollToContact}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-full text-lg transition duration-300"
            >
              Schedule a Free Helm Assessment
            </button>
          </div>
        </div>

        {/* FAQs */}
        <div className="max-w-3xl mx-auto p-6 py-20 w-full">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-center mb-8">
            FAQs
          </h2>
          {CicdFaqs.map((faq, index) => (
            <div key={index} className="border-b">
              <button
                onClick={() => toggleFAQ(index)}
                className={`w-full text-left py-4 flex justify-between items-center focus:outline-none ${
                  actIndex === index
                    ? "bg-blue-600 text-white px-5 rounded-2xl"
                    : ""
                }`}
              >
                <span className="text-lg">{faq.question}</span>
                <span className="text-2xl">
                  {actIndex === index ? "-" : "+"}
                </span>
              </button>
              {actIndex === index && (
                <div className="p-4 text-gray-700">{faq.answer}</div>
              )}
            </div>
          ))}
        </div>

        {/* CTA */}
        <div id="contact-section">
          <ContactUs />
        </div>
      </div>
    </>
  );
};

export default HelmCharts;

